"use client";

// SNOWCLUB-LAUNCH-REMAINING-PARITY-001 — THE GA4 `purchase` EVENT, EMITTED EXACTLY ONCE.
//
// WHAT WAS MEASURED, AND WHY THIS EXISTS. The consent/tracking observation banked in this packet
// (docs/evidence/snowclub-launch-remaining-parity-001/CONSENT-PRODUCTION-HOST-OBSERVATION.json,
// `purchase_or_ecommerce_event_commands: []`) recorded that NEITHER side emits a commerce event:
// live fires four `/g/collect` hits per page load and not one of them is a `purchase`. So this is
// not a parity restoration — there is nothing on live to restore. It is the one new analytics
// signal this packet adds, and it is added under the same three rails everything else here obeys.
//
// ═══ THE THREE RAILS ═══
//
// 1. ONLY AFTER A CONFIRMED PAYMENT, AS THE SERVER STATES IT. There are two call sites:
//    · `BookingSteps`' legacy submit — `booked === true && orderId` back from the engine (the §16
//      honesty rail: "never by a 200, never by the absence of an error");
//    · the review page's in-page card (@/lib/inpage-card `inpagePurchase`) — once the signed webhook
//      has written `pending_acceptance` or `paid` on a card-authorized order. FOUNDER RULING
//      2026-09-30: a card-authorized order awaiting the operator counts as a purchase for analytics
//      although nothing is captured yet; the later `paid` read is the same order id, so it is a
//      duplicate here, never a second sale. That is a measurement ruling only — the customer is
//      still told "authorised", never "paid", until the server reads `paid`.
//    A staged booking, a draft, a refusal, a failed card, a timed-out poll and a network failure all
//    emit NOTHING, because none of them is a sale.
//
// 2. NEVER FOR A PREVIEW. `booking-pay-bridge`'s founder-preview branch also returns `booked: true`
//    — deliberately, so the founder can walk to the confirmation screen with nothing transmitted.
//    That is not a sale and must never reach a revenue report. It is excluded structurally (the
//    preview branch does not call this module) AND defensively here, by refusing any transaction id
//    that says it is not a real booking. Two independent guards, because a revenue number that
//    silently includes preview walks is worse than no revenue number.
//
// 3. ONLY THROUGH THE EXISTING TAG. @/lib/analytics-init initializes gtag (analytics granted, advertising
//    denied). This module never creates a dataLayer or loads the tag itself, and no advertising choice
//    gates a real order's purchase.
//
// ═══ REPLAY / IDEMPOTENCY ═══
// The confirmation screen is reachable more than once for one order: React re-renders, the export
// iframe can re-post `sg:pay`, and a customer can reload or press back-then-forward on a confirmed
// booking. GA4 de-duplicates nothing for you — each `purchase` command is another transaction in
// the report. So the transaction id is recorded when it is emitted, in two places with different
// lifetimes, and a second emission for the same id is refused:
//
//   · an in-memory Set — survives re-renders and re-posts within one page load, and is the ONLY
//     protection a visitor with storage disabled has (private mode, blocked cookies). It is
//     checked first because it is the one that cannot fail.
//   · `localStorage["sg.ga4.purchases"]` — survives reloads, back/forward and a new tab. Capped at
//     `MAX_IDS` entries and `RETENTION_MS` old, because an unbounded ledger of every order a
//     browser ever placed is both a storage leak and a small pile of personal history we have no
//     reason to keep. The cap is a de-duplication window, and it is stated as one: an order
//     re-confirmed after 50 later orders or 30 days would emit again. That is an accepted and
//     bounded miss, not an oversight.
//
// Nothing here is a server-side guard, and this module does not pretend to be one: a browser event
// is de-duplicated in the browser. Server-side purchase truth is the payments capture work (GAP-1),
// which this packet does not build.
import { adPurchase } from "@/lib/ad-pixels";
import { isLiveHost } from "@/lib/analytics-init";

export interface PurchaseItem {
  item_id: string;
  item_name: string;
  quantity: number;
  price: number;
  item_brand?: string;
  item_category?: string;
}

export interface PurchaseEvent {
  /** The engine's own order id. GA4's `transaction_id`, and the de-duplication key. */
  transactionId: string;
  value: number;
  /** Defaults to CAD — the currency every captured fare on this site is quoted in. */
  currency?: string;
  /** Tax, when the server priced it separately — NOT part of `value` (GA4: value = Σ item price × quantity). */
  tax?: number;
  items: PurchaseItem[];
}

/**
 * Why an outcome string rather than a boolean: every refusal below is a DIFFERENT fact about the
 * visit, and a test (or a future reader chasing "why is there no purchase event") needs to tell
 * "they declined analytics" from "that was a preview" from "we already counted this one".
 */
export type PurchaseOutcome =
  | "emitted"
  | "duplicate"
  | "no-window"
  | "not-live-host"
  | "tag-not-initialised"
  | "no-transaction-id"
  | "not-a-real-order";

const STORE_KEY = "sg.ga4.purchases";
/** Long enough to cover any plausible re-visit to a confirmation screen; short enough not to hoard. */
const RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_IDS = 50;

/** Rail 2's defensive half. `booking-pay-bridge` labels its preview order "PREVIEW — NOT A REAL
 *  BOOKING" precisely so the screen cannot be misread; an id that says that must not be counted as
 *  revenue even if a future call site forgets which branch it is on. */
const NOT_A_REAL_ORDER = /preview|not a real/i;

interface Recorded {
  id: string;
  at: number;
}

/** Survives re-renders and re-posts within one page load; the only guard when storage is blocked. */
const emittedThisLoad = new Set<string>();

function readLedger(): Recorded[] {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is Recorded =>
        !!r && typeof (r as Recorded).id === "string" && typeof (r as Recorded).at === "number",
    );
  } catch {
    return []; // private mode, cleared storage, corrupt value — fall back to the in-memory guard
  }
}

function writeLedger(rows: Recorded[]): void {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(rows));
  } catch {
    /* storage unavailable: `emittedThisLoad` still covers this page load, which is the common replay */
  }
}

/** Has this transaction already been counted? Memory first — it is the guard that cannot fail. */
export function hasEmittedPurchase(transactionId: string): boolean {
  if (emittedThisLoad.has(transactionId)) return true;
  if (typeof window === "undefined") return false;
  const cutoff = Date.now() - RETENTION_MS;
  return readLedger().some((r) => r.id === transactionId && r.at > cutoff);
}

function recordEmitted(transactionId: string): void {
  emittedThisLoad.add(transactionId);
  if (typeof window === "undefined") return;
  const cutoff = Date.now() - RETENTION_MS;
  const rows = readLedger()
    .filter((r) => r.id !== transactionId && r.at > cutoff)
    .concat({ id: transactionId, at: Date.now() })
    .slice(-MAX_IDS);
  writeLedger(rows);
}

/**
 * Emit GA4 `purchase` for a confirmed order, at most once per transaction id.
 *
 * Never throws: a booking that really happened must not be turned into an error screen by a
 * measurement call, so every failure path here returns a reason instead.
 */
export function emitPurchaseOnce(event: PurchaseEvent): PurchaseOutcome {
  if (typeof window === "undefined") return "no-window";

  const transactionId = String(event.transactionId ?? "").trim();
  if (!transactionId) return "no-transaction-id";
  if (NOT_A_REAL_ORDER.test(transactionId)) return "not-a-real-order";

  // The advertising Purchase seam — reached only past the two rails above, so it inherits this function's
  // confirmed-order call sites and the preview refusal. It keeps its own advertising-consent gate and per-order
  // ledger (@/lib/ad-pixels): a GA4 duplicate or closed GA4 gate does not decide it.
  adPurchase(transactionId, Number.isFinite(event.value) ? event.value : 0, event.items ?? [], event.currency ?? "CAD");

  // Checked BEFORE the host/tag gates so a replay is reported as a replay rather than being
  // masked by whichever gate happens to be closed — the dedupe ledger is the fact under test here.
  if (hasEmittedPurchase(transactionId)) return "duplicate";

  if (!isLiveHost(window.location.hostname)) return "not-live-host";
  const gtag = (window as { gtag?: (...args: unknown[]) => void }).gtag;
  // No tag, no event, and deliberately no attempt to load one: @/lib/analytics-init owns tag setup.
  if (typeof gtag !== "function") return "tag-not-initialised";

  // Recorded BEFORE the call. If `gtag` throws (an extension has replaced it, the tag is being torn
  // down mid-navigation), the safe failure is one lost event, never a doubled one.
  recordEmitted(transactionId);
  try {
    gtag("event", "purchase", {
      transaction_id: transactionId,
      value: Number.isFinite(event.value) ? event.value : 0,
      currency: event.currency ?? "CAD",
      ...(typeof event.tax === "number" && Number.isFinite(event.tax) ? { tax: event.tax } : {}),
      items: event.items ?? [],
    });
  } catch {
    /* measurement is never allowed to break a confirmed booking */
  }
  return "emitted";
}

/** Test-only: the in-memory half of the guard is module state, so a suite exercising several
 *  transactions needs a way back to a clean slate between them. */
export function _resetPurchaseAnalyticsForTests(): void {
  emittedThisLoad.clear();
  try {
    window.localStorage.removeItem(STORE_KEY);
  } catch {
    /* nothing to clear */
  }
}
